import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { NavLink } from '../imports/generalImport';
import { Container, Skeleton, Box, useMediaQuery } from '../imports/muiImport';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { getSliderImages } from '../apiCalls/urls/urlFunction';

export const HeroSlider = ({ height = 181, showPagination = true }) => {
    const [sliderData, setSliderData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const isMobile = useMediaQuery('(max-width:600px)');

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
        Autoplay({ delay: 3000, stopOnInteraction: false }),
    ]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState([]);

    useEffect(() => {
        const fetchSliderData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const response = await getSliderImages();
                setSliderData(response?.data?.data);
            } catch (err) {
                console.error('Failed to fetch slider data:', err);
                setError('Failed to load sliders');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSliderData();
    }, []);

    const filteredSliders = useMemo(() => {
        if (!sliderData || !Array.isArray(sliderData)) return [];
        return sliderData.filter((item) => item && (isMobile ? item?.mobileSliderImage : item?.image));
    }, [isMobile, sliderData]);

    const scrollTo = useCallback((index) => emblaApi && emblaApi.scrollTo(index), [emblaApi]);

    const onInit = useCallback((emblaApi) => {
        setScrollSnaps(emblaApi.scrollSnapList());
    }, []);

    const onSelect = useCallback((emblaApi) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onInit(emblaApi);
        onSelect(emblaApi);
        emblaApi.on('reInit', onInit);
        emblaApi.on('reInit', onSelect);
        emblaApi.on('select', onSelect);
    }, [emblaApi, onInit, onSelect]);

    if (error) {
        return (
            <Container
                maxWidth="false"
                disableGutters
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: { xs: height, sm: '260px', md: '320px' },
                    backgroundColor: 'grey.100',
                    borderRadius: { xs: 2, sm: 0 },
                    color: 'error.main',
                    fontSize: '14px',
                }}
            >
                {error}
            </Container>
        );
    }

    return (
        <Container
            maxWidth="false"
            disableGutters
            sx={{
                pt: {
                    xs: 3,
                    md: 0,
                },
                position: 'relative',
                userSelect: 'none',
                touchAction: 'pan-y',
            }}
        >
            {isLoading ? (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width="100%"
                    sx={{
                        height: {
                            xs: height,
                            sm: '260px',
                            md: '320px',
                        },
                    }}
                />
            ) : (
                filteredSliders?.length > 0 && (
                    <Box sx={{ position: 'relative' }}>
                        <Box sx={{ overflow: 'hidden' }} ref={emblaRef}>
                            <Box sx={{ display: 'flex' }}>
                                {filteredSliders.map((item, index) => {
                                    const customURL = `${item?.url}${item?.url_utm ? `?${item?.url_utm}` : ''}`;
                                    const key = `hero-slider-${item?.id || item?.url || item?.mobileSliderImage || item?.image || index
                                        }`;

                                    const slideContent = (
                                        <SliderImage item={item} height={height} isMobile={isMobile} />
                                    );

                                    return (
                                        <Box
                                            key={key}
                                            sx={{ flex: '0 0 100%', minWidth: 0, position: 'relative' }}
                                        >
                                            {item?.url ? (
                                                <NavLink to={customURL} style={{ display: 'block', height: '100%' }}>
                                                    {slideContent}
                                                </NavLink>
                                            ) : (
                                                slideContent
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {showPagination && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 2,
                                    position: 'absolute',
                                    bottom: '8px',
                                    left: 0,
                                    right: 0,
                                    zIndex: 10,
                                }}
                            >
                                {scrollSnaps.map((_, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => scrollTo(index)}
                                        sx={{
                                            width: selectedIndex === index ? 24 : 8,
                                            height: 8,
                                            mx: 0.5,
                                            backgroundColor:
                                                selectedIndex === index ? 'primary.main' : 'rgba(191, 191, 191, 0.2)',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            border: selectedIndex === index ? 'none' : '1px solid #BFBFBF',
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                )
            )}
        </Container>
    );
};

const SliderImage = memo(({ item, height, isMobile }) => {
    const imageSrc = useMemo(
        () => (isMobile ? item?.mobileSliderImage : item?.image),
        [isMobile, item]
    );

    return (
        <Box
            component="img"
            src={imageSrc}
            alt={`${item?.title || 'slider'}-99gift-hero-sliders`}
            loading="lazy"
            sx={{
                width: { xs: '100%', md: '100vw' },
                height: {
                    xs: height,
                    sm: '260px',
                    md: 'auto',
                },
                objectFit: {
                    xs: 'contain',
                    sm: 'contain',
                },
                objectPosition: 'center',
                borderRadius: { xs: 2, sm: 0 },
                backgroundColor: { xs: 'white.main', sm: 'backgroundPrimary.main' },
                userSelect: 'none',
                // pointerEvents: 'none',
                display: 'block',
            }}
        />
    );
});
